/**
 * Generic formatting utilities using Intl APIs.
 */

import { capitalizeFirst, pluralize } from './string';

/**
 * Converts a Date object or ISO date string to a Date object.
 *
 * @param value - The Date object or ISO string to convert
 * @returns A Date object
 */
export function toDate(value: Date | string): Date {
  return typeof value === 'string' ? new Date(value) : value;
}

const defaultDateFormatOptions: Intl.DateTimeFormatOptions = {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
} as const;

/**
 * Reusable Intl.DateTimeFormat option presets for common date display formats.
 * Spread with additional options for compound formats (e.g. with time):
 *
 * @example
 * formatDate(date, DATE_FORMATS.short)              // "01/15/24"
 * formatDate(date, { ...DATE_FORMATS.short, hour: '2-digit', minute: '2-digit' }) // with time
 */
export const DATE_FORMATS = {
  /** "01/15/24" — Compact 2-digit date */
  short: { month: '2-digit', day: '2-digit', year: '2-digit' },
  /** "01/15/2024" — Compact 2-digit date with full year */
  medium: { month: '2-digit', day: '2-digit', year: 'numeric' },
  /** "1/15/24" — Numeric date with 2-digit year */
  numericShort: { month: 'numeric', day: 'numeric', year: '2-digit' },
  /** "1/15/2024" — Numeric date with full year */
  numeric: { month: 'numeric', day: 'numeric', year: 'numeric' },
  /** "January 15, 2024" — Long date format */
  long: { month: 'long', day: 'numeric', year: 'numeric' },
  /** "Jan 2024" — Month and year only */
  monthYear: { month: 'short', year: 'numeric' },
} as const satisfies Record<string, Intl.DateTimeFormatOptions>;

export type DateFormatPreset = keyof typeof DATE_FORMATS;

const resolveDateFormatOptions = (
  options: Intl.DateTimeFormatOptions | DateFormatPreset
): Intl.DateTimeFormatOptions => (typeof options === 'string' ? DATE_FORMATS[options] : options);

/**
 * Formats a Date object using Intl.DateTimeFormat.
 *
 * @param date - The date to format
 * @param options - Intl.DateTimeFormat options, or a DATE_FORMATS preset name (defaults to 'Jan 1, 2024' format)
 * @returns Formatted date string
 *
 * @example
 * formatDate(date)            // "Jan 15, 2024" (default)
 * formatDate(date, 'short')   // "01/15/24"
 * formatDate(date, { ...DATE_FORMATS.short, hour: '2-digit', minute: '2-digit' })
 */
export const formatDate = (
  date: Date,
  options: Intl.DateTimeFormatOptions | DateFormatPreset = defaultDateFormatOptions
): string => new Intl.DateTimeFormat('en-us', resolveDateFormatOptions(options)).format(date);

/**
 * Formats a date string using Intl.DateTimeFormat.
 *
 * @param date - The date string to format
 * @param options - Intl.DateTimeFormat options, or a DATE_FORMATS preset name
 * @returns Formatted date string
 */
export const formatDateString = (
  date: string,
  options: Intl.DateTimeFormatOptions | DateFormatPreset = defaultDateFormatOptions
): string => formatDate(new Date(date), options);

/**
 * Returns the ISO date portion (YYYY-MM-DD) of a date in UTC.
 *
 * @example
 * getISODateString(new Date('2024-03-15T12:00:00Z')) // '2024-03-15'
 * getISODateString() // today's date in UTC
 */
export const getISODateString = (date: Date = new Date()): string => date.toISOString().slice(0, 10);

/**
 * Converts an epoch-milliseconds URL search param into an ISO 8601 string.
 *
 * `dateRange` table filters write their bounds to the URL as epoch milliseconds, while APIs
 * generally expect ISO 8601 — this bridges the two in `buildQueryParams`. Search params are
 * user-editable and are not validated by `sanitizeSearchParams`, so any value that doesn't
 * resolve to a representable date yields `undefined` (dropping the bound) rather than throwing.
 *
 * @param value - The raw search param value (epoch milliseconds as a string)
 * @returns ISO 8601 string, or undefined when the value is absent or not a valid timestamp
 *
 * @example
 * timestampParamToIso('1710504000000') // '2024-03-15T12:00:00.000Z'
 * timestampParamToIso('not-a-date')    // undefined
 */
export function timestampParamToIso(value?: string): string | undefined {
  if (!value) return undefined;
  const date = new Date(Number(value));
  if (Number.isNaN(date.getTime())) return undefined;
  return date.toISOString();
}

/**
 * Converts an epoch-milliseconds URL search param into a date-only string (YYYY-MM-DD),
 * preserving the calendar day shown in the (local-timezone) date picker.
 *
 * `dateRange` table filters write local midnight / local end-of-day timestamps to the URL
 * (see `updateDateRangeFilter`). Converting through `timestampParamToIso` and truncating to
 * 10 characters takes the *UTC* calendar day, which is off by one in any timezone that isn't
 * UTC — e.g. local 23:59:59.999 on Aug 31 in America/New_York is 03:59:59Z on Sep 1. This takes
 * the local calendar day directly, matching what the user picked, for an API expecting a
 * date-only (no time component) field.
 *
 * @param value - The raw search param value (epoch milliseconds as a string)
 * @returns YYYY-MM-DD string, or undefined when the value is absent or not a valid timestamp
 *
 * @example
 * timestampParamToLocalDateString('1710504000000') // '2024-03-15' (the local calendar day)
 * timestampParamToLocalDateString('not-a-date')     // undefined
 */
export function timestampParamToLocalDateString(value?: string): string | undefined {
  if (!value) return undefined;
  const date = new Date(Number(value));
  if (Number.isNaN(date.getTime())) return undefined;
  return toUTCStartOfDay(date).slice(0, 10);
}

/**
 * Formats a date with time in UTC timezone.
 *
 * @param value - The Date object or ISO string to format
 * @returns Formatted string in format "M/D/YY at H:MMAM/PM UTC"
 */
export const formatDateTime = (value: Date | string): string => {
  const date = toDate(value);
  const dateStr = formatDate(date, { month: 'numeric', day: 'numeric', year: '2-digit', timeZone: 'UTC' });
  const timeStr = formatDate(date, { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'UTC' }).replace(
    ' ',
    ''
  );
  return `${dateStr} at ${timeStr} UTC`;
};

/**
 * Formats a number as a percentage.
 *
 * @param value - The decimal value to format (e.g., 0.5 for 50%)
 * @param options - Intl.NumberFormat options
 * @returns Formatted percentage string
 */
export const formatPercent = (value: number, options?: Intl.NumberFormatOptions): string => {
  return new Intl.NumberFormat('en-US', { style: 'percent', maximumFractionDigits: 2, ...options }).format(value);
};

/**
 * Formats a number with thousand separators using the en-US locale.
 * A minimumFractionDigits value alone raises maximumFractionDigits to match.
 *
 * @param value - The number to format
 * @param options - Intl.NumberFormat options merged over the defaults
 * @returns Formatted number string
 */
export function formatNumber(value: number, options?: Intl.NumberFormatOptions): string {
  const mergedOptions = {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    ...options,
  };

  if (options?.maximumFractionDigits === undefined) {
    mergedOptions.maximumFractionDigits = Math.max(
      mergedOptions.minimumFractionDigits ?? 0,
      mergedOptions.maximumFractionDigits ?? 0
    );
  }

  return new Intl.NumberFormat('en-US', mergedOptions).format(value);
}

/**
 * Groups the integer part of a decimal string with thousand separators, keeping the fractional
 * part exactly as given. Operates on the string rather than routing through a float, so a
 * full-precision decimal keeps every digit. Returns the input unchanged when it is not a plain
 * decimal.
 *
 * `minFractionDigits` pads the fraction with trailing zeros up to that width (e.g. a currency's
 * canonical decimals, so `3.4` reads `3.40`). It only ever pads: a value with more digits keeps
 * them all, so the amount is never rounded or truncated.
 */
export function formatDecimalString(value: string, minFractionDigits = 0): string {
  const match = /^(-?)(\d+)(?:\.(\d+))?$/.exec(value.trim());
  if (!match) return value;
  const [, sign, integer, fractionDigits = ''] = match;
  const groupedInteger = integer.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const fraction = fractionDigits.padEnd(minFractionDigits, '0');
  return fraction ? `${sign}${groupedInteger}.${fraction}` : `${sign}${groupedInteger}`;
}

/**
 * Formats a crypto amount with 2–8 fraction digits and thousand separators.
 *
 * @param value - The number to format
 * @returns Formatted crypto amount string
 */
export function formatCryptoAmount(value: number): string {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 8,
  }).format(value);
}

/**
 * Formats a number as compact currency with an order-of-magnitude suffix, e.g.
 * 22_000 -> "$22K", 500_000_000 -> "$500M", 1_250_000_000 -> "$1.25B", 1e12 -> "$1T".
 * Uses Intl compact notation so K/M/B/T scaling is applied canonically rather than
 * hand-dividing by a single fixed magnitude.
 *
 * @param value - The amount to format
 * @param currency - ISO currency code for the symbol (default: 'USD')
 * @returns Compact currency string
 */
export function formatCompactCurrency(value: number, currency: string = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    notation: 'compact',
    maximumFractionDigits: 2,
  }).format(value);
}

/**
 * Truncates an address or hash with ellipsis in the middle.
 *
 * @param address - The address string to truncate
 * @param leadingDigits - Number of characters to show at start (default: 2)
 * @param trailingDigits - Number of characters to show at end (default: 4)
 * @returns Truncated string with ellipsis
 */
export const truncateAddress = (address: string, leadingDigits: number = 2, trailingDigits: number = 4): string =>
  `${address.slice(0, leadingDigits)}...${address.slice(-trailingDigits)}`;

/**
 * Truncates text with ellipsis.
 *
 * @param text - The text to truncate
 * @param length - Maximum length before truncation
 * @param options - Optional settings (middle: truncate from middle instead of end)
 * @returns Truncated text with ellipsis
 */
export const truncateText = <T extends string, L extends number>(
  text: T,
  length: L,
  options?: { middle?: boolean }
) => {
  if (text.length <= length) return text;

  if (options?.middle) {
    const startLength = Math.ceil(length / 2);
    const endLength = Math.floor(length / 2);
    return `${text.slice(0, startLength)}...${text.slice(-endLength)}`;
  }

  return `${text.slice(0, length)}...`;
};

/**
 * Sorts an object's entries by their numeric values in descending order,
 * optionally taking the top N. Useful for "top breakdowns" in metric cards.
 *
 * @param obj - Record with numeric values
 * @param limit - Optional max number of entries to return
 * @returns Sorted [key, value] tuples in descending order
 */
export const sortObjectByValue = <T extends number>(
  obj: Record<string, T>,
  limit?: number
): [string, T][] => {
  const sorted = (Object.entries(obj) as [string, T][]).sort(([, a], [, b]) => b - a);
  return limit !== undefined ? sorted.slice(0, limit) : sorted;
};

/**
 * Formats a future date as a relative time string (e.g. "in 47 minutes", "in 2 hours").
 * Returns null if the date is in the past.
 *
 * @param date - The future date to format
 * @returns Relative time string or null if the date is in the past
 */
export function formatRelativeTime(date: Date | string): string | null {
  const target = toDate(date);
  const now = new Date();
  const diffMs = target.getTime() - now.getTime();

  if (diffMs <= 0) return null;

  const diffMinutes = Math.round(diffMs / 60_000);
  if (diffMinutes < 60) {
    return `in ${diffMinutes} ${pluralize(diffMinutes, 'minute')}`;
  }

  const diffHours = Math.round(diffMs / 3_600_000);
  if (diffHours < 24) {
    return `in ${diffHours} ${pluralize(diffHours, 'hour')}`;
  }

  const diffDays = Math.round(diffMs / 86_400_000);
  return `in ${diffDays} ${pluralize(diffDays, 'day')}`;
}

const RELATIVE_TIME_UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ['day', 86400],
  ['hour', 3600],
  ['minute', 60],
  ['second', 1],
];

/**
 * Formats a date as a relative time string using Intl.RelativeTimeFormat.
 * Works for both past and future dates (e.g., "2 hours ago", "yesterday", "in 3 days").
 *
 * @param date - The date to format (ISO string or epoch ms timestamp)
 * @param referenceTime - Optional reference time in epoch ms (defaults to Date.now()).
 *   Useful for triggering React re-renders with a ticking state value.
 */
export function formatTimeAgo(date: string | number | Date, referenceTime: number = Date.now()): string {
  const timestamp = date instanceof Date ? date.getTime() : typeof date === 'string' ? new Date(date).getTime() : date;
  const seconds = Math.round((referenceTime - timestamp) / 1000);
  const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });

  for (const [unit, threshold] of RELATIVE_TIME_UNITS) {
    if (Math.abs(seconds) >= threshold) {
      return rtf.format(-Math.round(seconds / threshold), unit);
    }
  }

  return rtf.format(-seconds, 'second');
}

const defaultListFormatOptions: Intl.ListFormatOptions = {
  style: 'long',
  type: 'conjunction',
} as const;

/**
 * Formats a list of strings into a human-readable string.
 *
 * @param list - Array of strings to format
 * @param options - Formatting options including locale, style, and type
 * @returns Formatted list string (e.g., "a, b, and c")
 */
export const formatList = (
  list: string[],
  {
    locale,
    ...restOptions
  }: {
    locale?: Intl.LocalesArgument;
    style?: Intl.ListFormatStyle;
    type?: Intl.ListFormatType;
  } = defaultListFormatOptions
): string => {
  const filteredList = list.filter(Boolean);
  return new Intl.ListFormat(locale, { ...restOptions }).format(filteredList);
};

/**
 * Converts a Date to a UTC midnight ISO string for the same calendar day shown in the picker.
 * Useful when a date picker returns a local-timezone Date but the API expects UTC midnight.
 *
 * @param date - The Date to normalize
 * @returns ISO 8601 string at midnight UTC (e.g., "2026-04-15T00:00:00.000Z")
 */
export function toUTCStartOfDay(date: Date | string): string {
  const d = toDate(date);
  return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())).toISOString();
}

/**
 * Returns the full hour closest to `hoursAhead` hours from now, in local time.
 * The result always lands on the hour (minutes/seconds/ms zeroed): 30+ minutes past
 * rounds up, otherwise down. e.g. at 14:20 with hoursAhead=3 -> 17:00; at 14:40 -> 18:00.
 * Useful for seeding a datetime picker with a sensible, tidy default.
 *
 * @param hoursAhead - How many hours ahead of now to target
 * @returns A Date on the closest full hour
 */
export function closestFullHourFromNow(hoursAhead: number): Date {
  const target = new Date();
  target.setHours(target.getHours() + hoursAhead);
  if (target.getMinutes() >= 30) {
    target.setHours(target.getHours() + 1);
  }
  target.setMinutes(0, 0, 0);
  return target;
}

/**
 * Splits a string by commas and/or newlines, trims each entry, and removes empty entries.
 * Useful for parsing user-supplied lists from textarea inputs.
 *
 * @param input - The raw string to parse
 * @param separator - Custom separator pattern (defaults to comma or newline)
 * @returns Array of non-empty trimmed strings
 */
export function parseDelimitedList(input: string, separator: RegExp = /[\n,]/): string[] {
  return input.split(separator).map((s) => s.trim()).filter(Boolean);
}

const ABBREVIATIONS = new Set(['id', 'uuid', 'kyb', 'kyc', 'kyt', 'rfi', 'sso', 'pk', 'api', 'c2c', 'bic']);

/**
 * Humanize a field name into a readable label.
 * Handles both snake_case and dot-separated (from nested flattening) names.
 * e.g. "slack_channel_name" → "Slack channel name"
 *      "developer.is_frozen" → "Developer is frozen"
 *      "kyb_email" → "KYB email"
 */
export function humanizeFieldName(name: string): string {
  return name
    .split(/[._]/)
    .map((word, i) => {
      if (ABBREVIATIONS.has(word)) return word.toUpperCase();
      if (i === 0) return capitalizeFirst(word);
      return word;
    })
    .join(' ');
}

/**
 * Stands in for a field with nothing in it. Two ASCII hyphens everywhere ensure tables,
 * detail panels, and currency formatting agree on what "no value" looks like.
 */
export const EMPTY_VALUE = '--';

/**
 * Whether a value of unknown shape carries nothing worth displaying: absent, blank,
 * or an empty bag (`[]`, `{}`).
 *
 * `0` and `false` are real readings of a field, so they are never empty — a reader
 * needs to tell "zero" apart from "we don't know".
 */
export function isEmptyValue(value: unknown): boolean {
  if (value == null || value === '') return true;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value).length === 0;
  return false;
}

/**
 * Renders a value of unknown shape as display text: an empty value becomes
 * {@link EMPTY_VALUE}, a boolean becomes "Yes"/"No", and an object or array is shown
 * as JSON.
 *
 * Booleans are spelled out rather than left as they are because React renders a
 * boolean child as nothing, so a `false` reading handed straight to JSX would
 * disappear from the page instead of reading as "No".
 */
export function formatUnknownValue(value: unknown): string {
  if (isEmptyValue(value)) return EMPTY_VALUE;
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

/**
 * Converts a decimal rate to basis points (bps) display string.
 * e.g. 0.003 → "30 bps", 0.0025 → "25 bps"
 */
export function formatRateToBps(rate: string): string {
  const bps = Math.round(parseFloat(rate) * 10000 * 100) / 100;
  return `${bps} bps`;
}

/**
 * Whole seconds elapsed since `timestamp`, floored at zero so clock skew never yields a negative span.
 *
 * @param timestamp - ISO string, epoch milliseconds, or Date
 * @param now - Reference instant in epoch milliseconds, defaults to the current time
 */
export function secondsSince(timestamp: string | number | Date, now: number = Date.now()): number {
  const then = timestamp instanceof Date ? timestamp.getTime() : new Date(timestamp).getTime();
  return Math.max(0, Math.floor((now - then) / 1000));
}

/**
 * Formats a duration in seconds as a human-readable string.
 * e.g. 90061 → "1d 1h", 3661 → "1h 1m", 45 → "< 1m"
 *
 * Shows days and hours, then hours and minutes (minutes are omitted when days are present).
 * Returns "< 1m" for zero or negative values.
 *
 * @param seconds - Duration in seconds
 * @returns Human-readable duration string
 */
export function formatDurationSeconds(seconds: number): string {
  if (seconds <= 0) return '< 1m';
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const parts: string[] = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0 && days === 0) parts.push(`${minutes}m`);
  return parts.join(' ') || '< 1m';
}

export interface FormatDurationOptions {
  /**
   * Floor the span to whole seconds, and word anything under one second as "< 1s".
   *
   * For spans measured between second-resolution timestamps, where a sub-second gap means both
   * instants fell inside the same second rather than "340ms" or no time at all.
   */
  secondPrecision?: boolean;
}

/**
 * Formats a short duration in milliseconds as a compact human-readable string,
 * with sub-second precision. Unlike formatDurationSeconds (which floors to minutes),
 * this is intended for fine-grained timings such as per-step latencies.
 *
 * e.g. 0 → "0s", 340 → "340ms", 1200 → "1.2s", 42000 → "42s",
 *      173000 → "2m 53s", 3661000 → "1h 1m", 90061000 → "1d 1h"
 *
 * Shows the two largest non-zero units (sub-10s durations show one decimal instead).
 *
 * @param ms - Duration in milliseconds
 * @param options - See FormatDurationOptions
 * @returns Compact duration string
 */
export function formatDurationMs(ms: number, options?: FormatDurationOptions): string {
  if (options?.secondPrecision) {
    const wholeSeconds = Math.floor(ms / 1000);
    if (!Number.isFinite(wholeSeconds) || wholeSeconds < 1) return '< 1s';
    return formatDurationMs(wholeSeconds * 1000);
  }

  if (!Number.isFinite(ms) || ms <= 0) return '0s';
  // Compare the rounded value so 999.6 reads "1s", not "1000ms" (same boundary shape as the
  // second/minute/hour rollovers below).
  if (Math.round(ms) < 1000) return `${Math.round(ms)}ms`;

  const totalSeconds = ms / 1000;
  if (totalSeconds < 10) return `${Math.round(totalSeconds * 10) / 10}s`;

  // Round to whole seconds before choosing the unit, so e.g. 59.95s reads "1m", not "60s".
  const totalSecondsInt = Math.round(totalSeconds);
  if (totalSecondsInt < 60) return `${totalSecondsInt}s`;

  const days = Math.floor(totalSecondsInt / 86400);
  const hours = Math.floor((totalSecondsInt % 86400) / 3600);
  const minutes = Math.floor((totalSecondsInt % 3600) / 60);
  const seconds = totalSecondsInt % 60;
  if (days > 0) return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
  if (hours > 0) return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
}

/**
 * Whole minutes between a past instant and now, formatted with thousands separators, or '-'
 * when the instant is missing or unparseable. Clamps negatives (a future or clock-skewed
 * timestamp) to 0 rather than rendering a negative age. Suited to a "Minutes elapsed" table
 * column that wants a raw count, not a compact "3h 20m" duration.
 *
 * @param from - Past instant (ISO8601 string or Date)
 * @param now - Reference "now" in ms since epoch; defaults to Date.now() (injectable for tests)
 * @returns Whole-minute count as a formatted string, or '-' when unmeasurable
 */
export function formatMinutesElapsed(from: string | Date | null | undefined, now: number = Date.now()): string {
  if (!from) return '-';
  const fromMs = from instanceof Date ? from.getTime() : Date.parse(from);
  if (Number.isNaN(fromMs)) return '-';
  return formatNumber(Math.max(0, Math.floor((now - fromMs) / 60_000)));
}

/**
 * Formats the span between two instants, or undefined when it cannot be measured: a missing or
 * unparseable bound, or `to` before `from`. formatDurationMs floors those to "0s", which would
 * dress bad data up as a measurement.
 *
 * @param from - Start instant (ISO8601 string or Date)
 * @param to - End instant (ISO8601 string or Date)
 * @param options - Passed through to formatDurationMs
 * @returns Compact duration string, or undefined when the span isn't measurable
 */
export function formatDurationBetween(
  from: string | Date | null | undefined,
  to: string | Date | null | undefined,
  options?: FormatDurationOptions
): string | undefined {
  if (!from || !to) return undefined;

  const ms = new Date(to).getTime() - new Date(from).getTime();
  if (!Number.isFinite(ms) || ms < 0) return undefined;

  return formatDurationMs(ms, options);
}
